import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { PRODUCT_PLACEHOLDER } from 'src/app/app.settings';
import {
  Allergens,
  AllergensNames,
  InnerLineMap,
  LineMap,
  OnlineStripContent,
  ProductDetails,
  ProductLine,
  ProductPackageOption,
  Saving,
} from 'src/app/models';
import { SharedDetailContent } from 'src/app/models/product-detail';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService } from 'src/app/services/content/content.service';
import { findPackageCombination } from 'src/app/services/product/product-package-combination';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { CenterSectionComponent, NutritionalContentComponent, TextButtonComponent } from 'src/app/shared/components';
import { ImageCarouselComponent } from 'src/app/shared/components/image-carousel';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { PriceFieldComponent } from 'src/app/shared/components/product/price-field/price-field.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { StripContainerComponent } from 'src/app/shared/layout/strips/strip-container.component';
import { environment } from 'src/environments/environment';
import { ProductDeliveryLimitPipe } from '../../../../../shared/pipes/delivery-limit.pipe';
import { PdfNamePipe } from '../../../../../shared/pipes/pdf-name.pipe';
import { SanitizeHtmlPipe } from '../../../../../shared/pipes/sanitize-html.pipe';
import { ProductAdditionsComponent } from '../../product-additions/product-additions.component';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';

@Component({
  selector: 'app-simple-product-detail',
  templateUrl: './simple-product-detail.component.html',
  styleUrls: ['./simple-product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CenterSectionComponent,
    ImageCarouselComponent,
    SanitizeHtmlPipe,
    PriceFieldComponent,
    DecimalPipe,
    AsyncPipe,
    TextButtonComponent,
    CounterComponent,
    ProductDeliveryLimitPipe,
    MatIconModule,
    DatePipe,
    NutritionalContentComponent,
    PdfNamePipe,
    ProductAdditionsComponent,
    StripContainerComponent,
    MatExpansionModule,
  ],
})
export class SimpleProductDetailComponent extends ContentSection implements AfterContentInit, OnChanges {
  private basketService = inject(BasketService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private dialogService = inject(DialogService);
  protected kioskService = inject(KioskService);

  contactUsUrl = `/${environment.urls.content.contact_us}`;
  locale = environment.i18n.language;

  @Input() product!: ProductDetails;

  @Input() sharedContent: SharedDetailContent;
  @Input() basketProducts: Observable<LineMap>;
  @Input() price: number;
  @Input() savings: Saving;

  @ViewChild('productContent', { read: ElementRef }) productContent!: ElementRef;
  @ViewChild('productDescription', { read: ElementRef }) productDescription!: ElementRef;

  placeholder = PRODUCT_PLACEHOLDER;
  originalPriceLabel: string;

  assortmentNames: Record<string, string> = {};

  preparationGuideLabel: string;
  summaryLabel: string;
  isDagrofa: boolean;
  strips: OnlineStripContent[];

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productInfo');

    this.preparationGuideLabel = this.content.get('preparationGuideText') || 'Tilberedningsvejledning';

    const sharedContent = this.contentService.getSharedContent();
    this.originalPriceLabel = sharedContent.get('originalPriceLabelText');

    this.isDagrofa = this.contentService.isDagrofa;
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['product']) {
      this.summaryLabel = this.product.summary;
      if (this.isDagrofa && this.product.minCount > 1) {
        this.summaryLabel = `Minimum ${this.product.minCount} kuverter`;
      }

      this.product.descriptionExtensions.sort((a, b) => a.index - b.index);
      this.strips = this.contentService.getOnlineProductStripContent(this.product.id);
    }
  }

  addToBasket() {
    let quantity = this.product.minCount;
    if (!this.product.packageOptions?.length) return this.addProduct(quantity);

    const packageOptions = [...this.product.packageOptions].sort((a, b) => b.packageSize - a.packageSize).map((o) => Object.assign({}, o));
    const packageSizes = packageOptions.map((o) => o.packageSize);
    let [sizeOne, sizeTwo] = findPackageCombination(quantity, packageSizes);

    if (sizeOne < 0) {
      quantity += sizeTwo;
      [sizeOne, sizeTwo] = findPackageCombination(quantity, packageSizes);
    }

    packageOptions[0].amount = sizeOne;
    if (packageOptions[1]) packageOptions[1].amount = sizeTwo;
    this.addProduct(quantity, packageOptions);
  }

  addProduct(quantity: number, packageOptions?: ProductPackageOption[]) {
    quantity = quantity || 1;

    this.basketService.addProduct(this.product, quantity, packageOptions, true);
  }

  removeProduct(prevQuantity: number, quantity: number, packageOptions?: ProductPackageOption[]) {
    quantity = quantity || 1;

    if (prevQuantity === quantity) {
      this.basketService.deleteProduct(this.product);
      return;
    }

    this.basketService.removeProduct(this.product, quantity, packageOptions);
  }

  isProductLine(line: ProductLine | InnerLineMap): ProductLine {
    return line as ProductLine;
  }

  getAllergensString(allergens: Allergens[]): string {
    const allergensNames = allergens.map((allergen) => AllergensNames[allergen]);
    return allergensNames.join(', ');
  }

  openLinkDialog(label: string, url: string) {
    this.dialogService.openLinkDialog(label, { url });
  }
}
